import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  
  useEffect(() => {
    // Verificar se já está instalado
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    
    // Verificar se é iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Capturar evento beforeinstallprompt (Android/Chrome)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Verificar se usuário já recusou
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setShowPrompt(true);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Verificar se foi instalado
    window.addEventListener('appinstalled', () => {
      console.log('✅ PWA instalado!');
      setShowPrompt(false);
      setDeferredPrompt(null);
    });
    
    // Mostrar prompt para iOS se não estiver instalado
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed-ios');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`PWA install: ${outcome}`);
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };
  
  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem(isIOS ? 'pwa-install-dismissed-ios' : 'pwa-install-dismissed', 'true');
  };
  
  // Não mostrar se já está instalado ou se não há prompt
  if (isStandalone || (!showPrompt && !isIOS)) {
    return null;
  }
  
  if (!showPrompt) return null;
  
  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300">
      <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0D0D0D] border border-[#C79F5E]/30 rounded-2xl p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <img 
            src="/icons/icon-96x96.png" 
            alt="LYBERHOT" 
            className="w-14 h-14 rounded-xl"
          />
          <div className="flex-1">
            <h3 className="text-white font-bold text-lg">Instalar LYBERHOT</h3>
            <p className="text-gray-400 text-sm mt-1">
              {isIOS 
                ? 'Adicione à tela inicial para acesso rápido!' 
                : 'Instale o app para uma experiência melhor!'}
            </p>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-gray-500 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>
        
        {isIOS ? (
          <div className="mt-4 bg-[#0D0D0D]/50 rounded-lg p-3">
            <p className="text-sm text-gray-300">
              <span className="text-[#C79F5E]">1.</span> Toque em{' '}
              <span className="inline-flex items-center bg-[#2A2A2A] px-2 py-0.5 rounded">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                </svg>
              </span>{' '}
              (Compartilhar)
            </p>
            <p className="text-sm text-gray-300 mt-2">
              <span className="text-[#C79F5E]">2.</span> Selecione "Adicionar à Tela de Início"
            </p>
          </div>
        ) : (
          <div className="flex gap-2 mt-4">
            <Button
              onClick={handleDismiss}
              variant="outline"
              className="flex-1 border-[#C79F5E]/30"
            >
              Agora não
            </Button>
            <Button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-[#C79F5E] to-[#8B6914] text-black font-semibold"
            >
              📲 Instalar
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
